import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
    api_get,
    api_post,
    api_put,
    api_delete,
    apiError,
    getAdminToken,
} from "../lib/api";

const DataContext = createContext(null);

const DEFAULT_SETTINGS = {
    phone: "+91 80-75814740",
    email: "info@southtrip.in",
    whatsapp: "+918075814740",
    address: "",
    bookingsEnabled: true,
    promoText: "",
    bannerMessage: "",
};

export const DataProvider = ({ children }) => {
    const [cabs, setCabs] = useState([]);
    const [packages, setPackages] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [popularDestinations, setPopularDestinations] = useState([]);
    const [bookings, setBookings] = useState([]); // admin-side
    const [inquiries, setInquiries] = useState([]);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);

    const refreshPublic = useCallback(async () => {
        try {
            const [cs, ps, ts, dests, pops, st] = await Promise.all([
                api_get("/cabs"),
                api_get("/packages"),
                api_get("/transfers"),
                api_get("/destinations"),
                api_get("/home/popular-destinations"),
                api_get("/settings"),
            ]);
            setCabs(cs);
            setPackages(ps);
            setTransfers(ts);
            setDestinations(dests);
            setPopularDestinations(pops);
            setSettings({ ...DEFAULT_SETTINGS, ...st });
        } catch (err) {
            console.error("Public fetch failed:", apiError(err));
        }
    }, []);

    const refreshAdminAll = useCallback(async () => {
        if (!getAdminToken()) return;
        try {
            const [allCabs, allPkgs, allTransfers, allBookings, allInq] = await Promise.all([
                api_get("/admin/cabs"),
                api_get("/admin/packages"),
                api_get("/admin/transfers"),
                api_get("/admin/bookings"),
                api_get("/admin/inquiries"),
            ]);
            setCabs(allCabs);
            setPackages(allPkgs);
            setTransfers(allTransfers);
            setBookings(allBookings);
            setInquiries(allInq);
        } catch (err) {
            console.error("Admin fetch failed:", apiError(err));
        }
    }, []);

    useEffect(() => {
        refreshPublic().finally(() => setLoading(false));
    }, [refreshPublic]);

    /* ---------- CABS ---------- */
    const addCab = async (cab) => {
        const created = await api_post("/admin/cabs", cab);
        setCabs((p) => [...p, created]);
        return created;
    };
    const updateCab = async (id, patch) => {
        const updated = await api_put(`/admin/cabs/${id}`, patch);
        setCabs((p) => p.map((c) => (c.id === id ? updated : c)));
        return updated;
    };
    const deleteCab = async (id) => {
        await api_delete(`/admin/cabs/${id}`);
        setCabs((p) => p.filter((c) => c.id !== id));
    };

    /* ---------- PACKAGES ---------- */
    const addPackage = async (pkg) => {
        const created = await api_post("/admin/packages", pkg);
        setPackages((p) => [...p, created]);
        return created;
    };
    const updatePackage = async (id, patch) => {
        const updated = await api_put(`/admin/packages/${id}`, patch);
        setPackages((p) => p.map((x) => (x.id === id ? updated : x)));
        return updated;
    };
    const deletePackage = async (id) => {
        await api_delete(`/admin/packages/${id}`);
        setPackages((p) => p.filter((x) => x.id !== id));
    };
    const duplicatePackage = async (id) => {
        const created = await api_post(`/admin/packages/${id}/duplicate`, {});
        setPackages((p) => [...p, created]);
        return created;
    };

    /* ---------- TRANSFERS ---------- */
    const addTransfer = async (t) => {
        const created = await api_post("/admin/transfers", t);
        setTransfers((p) => [...p, created]);
        return created;
    };
    const updateTransfer = async (id, patch) => {
        const updated = await api_put(`/admin/transfers/${id}`, patch);
        setTransfers((p) => p.map((x) => (x.id === id ? updated : x)));
        return updated;
    };
    const deleteTransfer = async (id) => {
        await api_delete(`/admin/transfers/${id}`);
        setTransfers((p) => p.filter((x) => x.id !== id));
    };

    /* ---------- BOOKINGS ---------- */
    const createBooking = async (payload) => {
        const created = await api_post("/bookings", payload);
        return created;
    };
    const updateBooking = async (id, patch) => {
        const updated = await api_put(`/admin/bookings/${id}`, patch);
        setBookings((p) => p.map((b) => (b.id === id ? updated : b)));
        return updated;
    };
    const cancelBooking = async (id) => {
        const updated = await api_put(`/bookings/${id}/cancel`, {});
        return updated;
    };
    const fetchMyBookings = async () => api_get("/bookings/me");
    const fetchBookingById = async (id) => api_get(`/bookings/${id}`);

    /* ---------- INQUIRIES ---------- */
    const addInquiry = async (inq) => {
        const created = await api_post("/inquiries", inq);
        return created;
    };
    const updateInquiry = async (id, patch) => {
        const updated = await api_put(`/admin/inquiries/${id}`, patch);
        setInquiries((p) => p.map((i) => (i.id === id ? updated : i)));
        return updated;
    };
    const deleteInquiry = async (id) => {
        await api_delete(`/admin/inquiries/${id}`);
        setInquiries((p) => p.filter((i) => i.id !== id));
    };

    /* ---------- SETTINGS ---------- */
    const updateSettings = async (patch) => {
        const updated = await api_put("/admin/settings", patch);
        setSettings(updated);
        return updated;
    };

    /* ---------- BULK PRICING ---------- */
    const adjustAllCabPrices = async (percent) => {
        await api_post("/admin/pricing/cabs", { percent });
        await refreshAdminAll();
    };
    const adjustAllPackagePrices = async (percent) => {
        await api_post("/admin/pricing/packages", { percent });
        await refreshAdminAll();
    };

    return (
        <DataContext.Provider
            value={{
                cabs,
                packages,
                transfers,
                destinations,
                popularDestinations,
                bookings,
                inquiries,
                settings,
                loading,
                refreshPublic,
                refreshAdminAll,
                addCab,
                updateCab,
                deleteCab,
                addPackage,
                updatePackage,
                deletePackage,
                duplicatePackage,
                addTransfer,
                updateTransfer,
                deleteTransfer,
                createBooking,
                updateBooking,
                cancelBooking,
                fetchMyBookings,
                fetchBookingById,
                addInquiry,
                updateInquiry,
                deleteInquiry,
                updateSettings,
                adjustAllCabPrices,
                adjustAllPackagePrices,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    const ctx = useContext(DataContext);
    if (!ctx) throw new Error("useData must be used inside DataProvider");
    return ctx;
};
